// modules/bom.js — Kusovník (Bill of Materials): z čoho a koľko sa výrobok skladá.
// Zákazková výroba (businessTypes: stolár, kovovýroba…): výrobok = katalógová položka
// (module='stock_item') + zoznam ZLOŽIEK, každá je iná stock_item + množstvo na 1 kus výrobku.
//
// bom_line = records riadok (module='bom_line') — modul NIKDY nepridáva tabuľku (vzor sklad.js):
// qty_per → rezervovaný `qty` stĺpec kernelu, product_ref/component_ref → custom JSON (records má
// fixnú schému, nový top-level stĺpec by zhodil INSERT). 0 fetch / 0 LLM / 0 nových závislostí / 0 DDL.
//
// CROSS-DEVICE (vzor createJob df4aa1c + poLifecycle): linka má DETERMINISTICKÉ id
// `bomline-<product>-<component>` (id je PK) → dve zariadenia offline pridajú tú istú zložku →
// ROVNAKÉ id → sync ich zlúči (LWW) → JEDNA linka, nie dvojitý náklad. setBomLine je preto
// IDEMPOTENTNÝ (upsert qty), read-guard-write v db.transaction (engine mutex serializuje súbeh).
//
// NÁKLAD kusovníka = Σ qty_per × posledná NÁKUPNÁ cena zložky (reuse jitReorder.lastUnitCostCents,
// len direction='in'). Zložka bez známej ceny prispeje 0 (honest-null, vzor sklad.stockValueCents) —
// cena sa NIKDY nefabrikuje. NIE JE rekurzívny: zložka, ktorá je sama vyrábaná (má vlastný kusovník,
// ale žiadnu nákupnú cenu), prispeje 0 — jedna úroveň, žiadna cyklus/nekonečná rekurzia. Priama
// sebareferencia (výrobok == zložka) je zakázaná pri zápise.
import { db, newCommonFields } from '../db';
import { todayIso } from '../dateUtils';
import { stockLevel } from './sklad';
import { lastUnitCostCents } from '../flows/jitReorder';
import { sha256Hex } from '../migration/dedupe';

// ─── MODULE_DEF — deklaratívny descriptor (DÁTA, žiadna logika) ────────────────
export const MODULE_DEF = {
  module: 'bom',
  label_sk: 'Kusovník (BOM)',
  // accent = jediný zdroj brand farby modulu (menu+header+KPI).
  accent: 'yellow',
  entity: {
    base: 'record', // bom_line nad univerzálnym records enginom
    custom_fields: [
      { stable_id: 'f_qty_per', label: 'Množstvo na kus', type: 'number' },
    ],
  },
  view: { filter: { module: 'bom_line' }, sort: 'label_asc' },
  access: { collaborator: true, max_tier_out: 2 }, // kolaborant vidí zloženie, nie ceny
  metrics: [
    { slug: 'naklad_vyrobku', reduce: 'engine' }, // bomCostCents (per výrobok, nie aggregate)
    { slug: 'vyrobitelne_kusy', reduce: 'engine' }, // bomBuildableUnits
  ],
};

export const BOM_LINE_MODULE = 'bom_line';

// ─── Validátory ───────────────────────────────────────────────────────────────
const isNonEmpty = (v) => typeof v === 'string' && v.trim() !== '';
// Konečné kladné číslo (qty_per smie byť desatinné, napr. 2.5 kg materiálu na kus).
const isPositiveNumber = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0;

// Deterministické id linky — pár (výrobok, zložka) jednoznačne určuje jednu linku.
// HASH z JSON tuple, NIE konkatenácia separátorom: `bomline-<p>-<c>` je NE-injektívne, keď ref
// obsahuje '-' (uuid ho má) → `('a-b','c')` a `('a','b-c')` by dali rovnaké id → druhá linka by sa
// ticho stratila (data-loss pasca [[todue-v2-deterministic-id-injective]], Codex ju chytil pri
// timesheet). JSON.stringify escapuje → rôzny tuple = rôzny string → sha256Hex = rôzne (safe) id.
const lineId = (productRef, componentRef) => `bomline-${sha256Hex(JSON.stringify([productRef, componentRef]))}`;

// Živá bom_line (cudzie/poškodené records zo syncu sa ticho preskočia).
const isLiveBomLine = (r) =>
  r != null && r.module === BOM_LINE_MODULE && r.deleted_at == null;

// ─── setBomLine — nastav množstvo zložky vo výrobku (create ALEBO update, IDEMPOTENTNÝ) ─
// input = { product_ref (povinné, stock_item.id), component_ref (povinné, iná stock_item.id),
//           qty_per (> 0, koľko zložky na 1 kus výrobku) }
// Deterministické id + read-guard-write v tx: opakované volanie tej istej dvojice iba UPRAVÍ qty
// (žiadna dvojitá linka), soft-deletnutá linka OŽIJE. Chyby hádže (write-side gate).
export async function setBomLine(input) {
  const productRef = input?.product_ref;
  const componentRef = input?.component_ref;
  if (!isNonEmpty(productRef)) throw new Error('product_ref je povinný');
  if (!isNonEmpty(componentRef)) throw new Error('component_ref je povinný');
  if (productRef === componentRef) throw new Error('výrobok nemôže byť vlastnou zložkou (sebareferencia)');
  if (!isPositiveNumber(input.qty_per)) throw new Error('qty_per musí byť číslo > 0');

  const id = lineId(productRef, componentRef);
  return db.transaction(async (tx) => {
    const existing = await tx.records.get(id);
    const now = todayIso();
    if (existing) {
      // Update alebo resurrect — qty_per, vyčisti deleted_at, bumpni rev. Celý custom sa NAHRADZUJE,
      // preto ho spreadneme (product_ref/component_ref by inak zmizli).
      const rev = (existing.rev || 1) + 1;
      const custom = { ...(existing.custom || {}), product_ref: productRef, component_ref: componentRef };
      await tx.records.update(id, { qty: input.qty_per, deleted_at: null, custom, updated_at: now, rev });
      return { ...existing, qty: input.qty_per, deleted_at: null, custom, updated_at: now, rev };
    }
    const row = {
      ...newCommonFields(),
      id,
      module: BOM_LINE_MODULE,
      entry_date: now,
      qty: input.qty_per, // rezervovaný stĺpec kernelu
      unit: input.unit || 'ks',
      amount_cents: null,
      status: 'confirmed',
      source: 'manual',
      updated_at: now,
      custom: { product_ref: productRef, component_ref: componentRef },
    };
    await tx.records.add(row);
    return row;
  });
}

// ─── removeBomLine — soft-delete linky (ZÁSADA: žiadny hard delete, audit ostáva) ─
// Neexistujúca/už zmazaná linka = no-op (idempotentné). Vracia počet dotknutých riadkov.
export async function removeBomLine(productRef, componentRef) {
  if (!isNonEmpty(productRef) || !isNonEmpty(componentRef)) {
    throw new Error('product_ref aj component_ref sú povinné');
  }
  const id = lineId(productRef, componentRef);
  return db.transaction(async (tx) => {
    const existing = await tx.records.get(id);
    if (!existing || existing.deleted_at != null) return 0;
    const rev = (existing.rev || 1) + 1;
    await tx.records.update(id, { deleted_at: todayIso(), rev });
    return 1;
  });
}

// ─── Čisté reduce funkcie (records injektnuté, žiadne DB čítanie) ──────────────

// Živé linky daného výrobku.
export function bomLines(records, productRef) {
  const recs = Array.isArray(records) ? records : [];
  return recs.filter((r) => isLiveBomLine(r) && r.custom?.product_ref === productRef);
}

// Náklad 1 kusu výrobku v centoch = Σ qty_per × posledná nákupná cena zložky.
// Zložka bez známej ceny (lastUnitCostCents null) prispeje 0 — honest-null, cena sa nefabrikuje.
// Zaokrúhlenie RAZ per linka (qty_per smie byť desatinné, cena je int centy).
export function bomCostCents(records, productRef) {
  const recs = Array.isArray(records) ? records : [];
  let cents = 0;
  for (const l of bomLines(recs, productRef)) {
    const qtyPer = Number(l.qty);
    if (!Number.isFinite(qtyPer) || qtyPer <= 0) continue; // poškodená linka → 0 príspevok
    const unitCost = lastUnitCostCents(recs, l.custom.component_ref);
    if (!Number.isInteger(unitCost) || unitCost < 0) continue; // neznáma cena → 0 (honest-null)
    cents += Math.round(qtyPer * unitCost);
  }
  return cents;
}

// Koľko kusov výrobku sa dá vyrobiť z aktuálneho skladu = min cez zložky floor(sklad / qty_per).
// Bez receptu (žiadna linka) → null: nevieme (nefabrikuj 0 ani ∞). Zložka bez skladu → 0.
export function bomBuildableUnits(records, productRef) {
  const recs = Array.isArray(records) ? records : [];
  const lines = bomLines(recs, productRef);
  if (lines.length === 0) return null;
  let min = Infinity;
  for (const l of lines) {
    const qtyPer = Number(l.qty);
    if (!Number.isFinite(qtyPer) || qtyPer <= 0) continue; // poškodená linka → preskoč
    const have = stockLevel(recs, l.custom.component_ref);
    const buildable = Math.floor(have / qtyPer);
    if (buildable < min) min = buildable;
  }
  if (!Number.isFinite(min)) return null; // všetky linky poškodené
  return Math.max(0, min);
}

// Čo chýba na výrobu targetUnits kusov: per zložka { component_ref, need, have, short }.
// Vracia LEN zložky so short > 0, zoradené od najväčšieho deficitu (deterministicky, tie podľa ref).
// Komponuje sa s nákupom (procurement): short = koľko dokúpiť.
export function bomShortfall(records, productRef, targetUnits) {
  const recs = Array.isArray(records) ? records : [];
  const n = Number(targetUnits);
  if (!Number.isFinite(n) || n <= 0) return [];
  const out = [];
  for (const l of bomLines(recs, productRef)) {
    const qtyPer = Number(l.qty);
    if (!Number.isFinite(qtyPer) || qtyPer <= 0) continue;
    const componentRef = l.custom.component_ref;
    const need = qtyPer * n;
    const have = stockLevel(recs, componentRef);
    const short = need - have;
    if (short > 0) out.push({ component_ref: componentRef, need, have, short });
  }
  return out.sort((a, b) => (b.short - a.short) || (a.component_ref < b.component_ref ? -1 : 1));
}
